import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Person, Email, Phone, Language, Edit, Delete } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { DataTable } from '../../components/common/DataTable';
import { SupplierForm } from '../../components/forms/SupplierForm';
import { supplierService } from '../../services/supplierService';
import { Supplier } from '../../types';

// One line of contact info on a phone card; links so a tap calls or emails.
const ContactLine: React.FC<{ icon: React.ReactNode; text?: string; href?: string }> = ({ icon, text, href }) =>
  text ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, color: 'text.secondary' }}>
      {icon}
      <Typography
        variant="body2"
        component={href ? 'a' : 'span'}
        href={href}
        sx={{ color: href ? 'primary.main' : 'text.primary', textDecoration: 'none', overflowWrap: 'anywhere' }}
      >
        {text}
      </Typography>
    </Box>
  ) : null;

export const SupplierList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService.getSuppliers({ limit: 100 });
      setSuppliers(data);
    } catch (err: any) {
      setError('Failed to load suppliers');
      console.error('Suppliers fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedSupplier(null);
    setFormMode('create');
    setFormOpen(true);
  };

  const handleEdit = (id: string) => {
    const supplier = suppliers.find(s => s.supplier_id === id);
    if (supplier) {
      setSelectedSupplier(supplier);
      setFormMode('edit');
      setFormOpen(true);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await supplierService.deleteSupplier(id);
        await fetchSuppliers(); // Refresh the list
      } catch (err) {
        setError('Failed to delete supplier');
      }
    }
  };

  const handleView = (id: string) => {
    console.log('View supplier:', id);
  };

  const handleFormSubmit = async (supplier: Supplier) => {
    await fetchSuppliers(); // Refresh the list
    setFormOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Supplier Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'contact_person',
      headerName: 'Contact Person',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'website',
      headerName: 'Website',
      width: 120,
      renderCell: (params) => params.value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Language fontSize="small" color="action" />
          <Typography variant="body2" component="a" href={params.value} target="_blank" rel="noopener">
            Visit
          </Typography>
        </Box>
      ) : '-',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'create_time',
      headerName: 'Created',
      width: 120,
      type: 'date',
      valueGetter: (value: string) => new Date(value),
    },
  ];

  const rows = suppliers.map(supplier => ({
    id: supplier.supplier_id,
    ...supplier,
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Suppliers
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          {isMobile ? 'Add' : 'Add Supplier'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : suppliers.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            No suppliers yet.
          </Typography>
        ) : (
          suppliers.map((supplier) => (
            <Card key={supplier.supplier_id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
              <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1 } }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Typography variant="body1" fontWeight={600} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {supplier.name}
                    </Typography>
                    <Chip
                      label={supplier.is_active ? 'Active' : 'Inactive'}
                      color={supplier.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <ContactLine icon={<Person fontSize="small" />} text={supplier.contact_person} />
                  <ContactLine icon={<Phone fontSize="small" />} text={supplier.phone} href={supplier.phone ? `tel:${supplier.phone}` : undefined} />
                  <ContactLine icon={<Email fontSize="small" />} text={supplier.email} href={supplier.email ? `mailto:${supplier.email}` : undefined} />
                  {supplier.website && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                      <Language fontSize="small" />
                      <Typography variant="body2" component="a" href={supplier.website} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
                        Visit website
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                    <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(supplier.supplier_id)}>
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={() => handleDelete(supplier.supplier_id)}>
                      Delete
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))
        )
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          pageSize={50}
        />
      )}

      <SupplierForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        supplier={selectedSupplier}
        mode={formMode}
      />
    </Box>
  );
};
